import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO, PAGE_SEO } from "@/components/SEO";

const JOIN_URL = "https://app.lifely.guide/join/pricing";

const sections = [
  {
    id: "dashboard",
    title: "A clear starting point",
    image: "/images/inside-lifely/1 - dashboard.png",
    description: "The Dashboard gives you a complete overview of your estate setup at a glance.",
    details: "See what's configured, what still needs attention, your continuity status, and your next check-in date — all in one clear, structured view.",
    highlight: "It's not about overwhelm. It's about clarity.",
    cta: "Start with your overview",
  },
  {
    id: "contacts",
    title: "The people who matter",
    image: "/images/inside-lifely/2 - contacts.png",
    description: "Add family members, legal advisers, financial professionals, or anyone connected to your estate.",
    details: "These contacts power everything inside Lifely — from formal arrangements to sharing rules and continuity notifications.",
    highlight: "Your estate isn't just assets. It's relationships.",
    cta: "Add your key contacts",
  },
  {
    id: "formal",
    title: "Record what formally exists",
    image: "/images/inside-lifely/3 - formal arrangements.png",
    description: "Lifely does not store legal documents. Instead, you record their existence, location, and review dates.",
    details: null,
    list: [
      "Whether a will exists",
      "Where it's held",
      "When it was last reviewed",
      "Reminder dates",
    ],
    highlight: "This keeps your formal world aligned with your digital one.",
    cta: "Document your formal setup",
  },
  {
    id: "maps",
    title: "Map what exists. Not the sensitive details.",
    image: "/images/inside-lifely/4 - maps.png",
    description: "Organise different areas of your life into structured maps — crypto, valuables, insurance, liabilities, final wishes, and more. 12 Map categories.",
    details: null,
    list: [
      "What exists",
      "Where it's located",
      "Your intentions",
    ],
    listPrefix: "Each map documents:",
    highlight: "This is structured awareness — not risky storage.",
    note: "Without storing private keys, passwords, or legal files.",
    cta: "Create your first map",
  },
  {
    id: "continuity",
    title: "A simple check-in system",
    image: "/images/inside-lifely/5 - Continuity.png",
    description: "Lifely includes a continuity layer. You check in periodically.",
    details: "If you stop responding after multiple reminders, your chosen nominees are notified and given access to the information you've configured.",
    highlight: "No automatic sharing. No assumptions. Just a safety net you control.",
    cta: "Activate continuity",
  },
  {
    id: "export",
    title: "Professional, structured exports",
    image: "/images/inside-lifely/6 - export.png",
    description: "Generate clean, formatted summaries for:",
    list: [
      "Lawyers",
      "Family members",
      "Personal records",
    ],
    details: "Export only what you choose — asset maps, contacts, legal status, or everything.",
    highlight: "You stay in control of what leaves the platform.",
    cta: "See export options",
  },
  {
    id: "sharing",
    title: "Nothing is shared automatically",
    image: "/images/inside-lifely/7 - sharing.png",
    description: "Sharing only happens:",
    list: [
      "When triggered by your continuity rules",
      "Or when you manually export information",
    ],
    details: "Create clear sharing rules tied to specific people and categories.",
    highlight: "Precision access. Not blanket exposure.",
    cta: "Configure sharing",
  },
  {
    id: "insights",
    title: "Thought leadership for the digital age",
    image: "/images/inside-lifely/8 - insights .png",
    description: "Lifely isn't just a tool — it's education. Explore in-depth insights on:",
    list: [
      "Decentralisation and estate planning",
      "Crypto continuity",
      "Governance structures",
      "Digital legacy risks",
    ],
    highlight: "This is where traditional planning meets modern reality.",
    cta: "Read the featured insights",
  },
  {
    id: "guides",
    title: "Practical guidance by region",
    image: "/images/inside-lifely/9 - insights.png",
    description: "Access structured guides explaining:",
    list: [
      "Will requirements",
      "Execution rules",
      "Review best practices",
      "Jurisdiction differences",
    ],
    highlight: "Because laws change — and clarity matters.",
    note: "Simplified. Clear. Interpreted.",
    cta: "Explore estate guides",
  },
];

export default function InsideLifely() {
  return (
    <div className="pt-24">
      <SEO {...PAGE_SEO.insideLifely} />
      {/* Hero Section */}
      <section className="py-14 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4"
          >
            Inside Lifely
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground leading-tight"
          >
            Clarity for what matters most.
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mt-6 text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto"
          >
            Lifely is designed to help you organise your digital life, formal arrangements, 
            and continuity planning — without storing sensitive documents. Here's a look inside the members area.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a href={JOIN_URL}>
              <Button className="h-12 px-8 text-sm bg-foreground text-background hover:bg-foreground/90 rounded-full font-medium">
                Start Mapping What Matters
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <a href="#dashboard">
              <Button variant="outline" className="h-12 px-8 text-sm rounded-full font-medium">
                Explore the Features
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Feature Sections */}
      {sections.map((section, index) => (
        <section 
          key={section.id}
          id={section.id}
          className={`py-20 ${index % 2 === 1 ? 'bg-secondary/30' : ''}`}
        >
          <div className="max-w-6xl mx-auto px-6">
            <div className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={index % 2 === 1 ? 'lg:col-start-2' : ''}
              >
                <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
                  {section.title}
                </h2>
                
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {section.description}
                </p>

                {section.listPrefix && (
                  <p className="text-foreground font-medium mb-2">{section.listPrefix}</p>
                )}

                {section.list && (
                  <ul className="space-y-2 mb-4">
                    {section.list.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-muted-foreground">
                        <span className="text-primary mt-1">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.details && (
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    {section.details}
                  </p>
                )}

                {section.note && (
                  <p className="text-sm text-muted-foreground/80 mb-4 italic">
                    {section.note}
                  </p>
                )}

                <div className="bg-primary/5 border border-primary/10 rounded-xl p-5 mb-6">
                  <p className="text-foreground/90 font-medium">
                    {section.highlight}
                  </p>
                </div>

                <a 
                  href={JOIN_URL}
                  className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
                >
                  {section.cta} <ArrowRight className="w-4 h-4" />
                </a>
              </motion.div>

              {/* Image */}
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}
              >
                <div className="rounded-2xl overflow-hidden shadow-xl border border-border/50">
                  <img
                    src={section.image}
                    alt={section.title}
                    className="w-full h-auto"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* Final CTA Section */}
      <section className="py-24 bg-[#2D2A26] text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-white/60 text-lg mb-4"
          >
            There's so much more to explore inside Lifely.
          </motion.p>
          
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl mb-6"
          >
            Bring Structure to What Matters
          </motion.h2>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="space-y-2 text-white/70 text-lg mb-10"
          >
            <p>Estate planning hasn't evolved.</p>
            <p>Your assets have.</p>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="text-white/80 text-lg mb-10"
          >
            Lifely helps you bridge that gap — with clarity, security, and intention.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a href={JOIN_URL}>
              <Button className="h-12 px-8 bg-white text-[#2D2A26] hover:bg-white/90 rounded-full font-medium">
                Start Your Lifely Account
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <a href={JOIN_URL}>
              <Button 
                variant="outline" 
                className="h-12 px-8 rounded-full border-white/30 text-white hover:bg-white/10 font-medium"
              >
                View Pricing
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
